<template>
    <span
        v-if="renderNode"
        ref="node"
        :class="[typeClass, selectedClass, changedClass, levelClass]"
        @click.stop="onClick(node, $event)"
        @mouseenter="onHover(node, $event)"
        @mouseleave="onMouseLeave(node, $event)"
    >
        <!-- Vue 2 condensed the surrounding newlines into spaces (" text "),
             Vue 3 drops them entirely — keep them explicit so inline nodes
             (words, pauses) don't run together -->
        {{ ' ' + text + ' ' }}<SsmlTreeNode
            v-for="child in node.children"
            :key="child.id"
            :node="child"
            :tree-height="treeHeight"
        />
    </span>
</template>

<script>
import { isWordType } from '../../services/ssmlParser';

export default {
    name: 'SsmlTreeNode',
    inject: [
        'selectNode',
        'unselectNode',
        'clearSelection',
        'getSortedSelection',
        'isSelectable',
        'isSelected'
    ],
    props: {
        node: {
            type: Object,
            default: () => ({ speak: { name: 'speak', children: [] } })
        },
        treeHeight: {
            type: Number,
            default: 1
        }
    },
    computed: {
        renderNode() {
            return !(this.node.name === 'space' && this.node.parent.name === 'break');
        },
        typeClass() {
            const { type, name, children } = this.node;

            if (name === 'speak') {
                return 'ssml-visual__root';
            }

            if ((type === 'tag' && name === 'break') || type === 'space') {
                return 'ssml-visual__pause';
            }

            if (type === 'tag') {
                if (children.length === 1 && children[0].name === 'word') {
                    return '';
                }

                return 'ssml-visual__tag';
            }

            if (type === 'punctuation') {
                return 'ssml-visual__word is-disabled';
            }

            return 'ssml-visual__word';
        },
        selectedClass() {
            if (
                this.node.type === 'word' &&
                this.node.parent.children.length === 1 &&
                this.isSelected(this.node.parent) &&
                this.treeHeight
            ) {
                return 'is-active';
            }

            return this.isSelected(this.node) && 'is-active';
        },
        changedClass() {
            const { name, type, parent } = this.node;

            if (type === 'tag' && name === 'break') {
                return 'is-changed';
            }

            if (
                isWordType(type) &&
                parent.name !== 'speak' &&
                parent.children.length === 1 &&
                parent.children[0].name === 'word'
            ) {
                return 'is-changed';
            }

            return '';
        },
        levelClass() {
            const { name, type, parent, children } = this.node;

            if (['break', 'space', 'speak'].includes(name)) {
                return '';
            }

            // tag with only word should skip class
            if (
                type === 'tag' &&
                children.length === 1 &&
                (children[0].type === 'word' || children[0].type === 'number')
            ) {
                return '';
            }

            const height = this.treeHeight;

            // only word wrapped in tag should get ++level
            if (
                (type === 'word' || type === 'number') &&
                parent.name !== 'speak' &&
                parent.children.length === 1 &&
                parent.children[0].name === 'word'
            ) {
                return `level-${height - this.node.level + 1}`;
            }

            if (type === 'word' && parent.name === 'speak') {
                return `level-${height - this.node.level - 1}`;
            }

            return `level-${height - this.node.level}`;
        },
        text() {
            return this.node.text || '';
        }
    },
    methods: {
        onHover(node, event) {
            if (node.name === 'speak') return;

            const notSelectable =
                (event.metaKey || event.ctrlKey || event.shiftKey) && !this.isSelectable(node);

            if (notSelectable) {
                this.$refs.node.classList.add('non-selectable');
            } else {
                this.$refs.node.classList.add('hovered');
            }
        },
        onMouseLeave(node, event) {
            if (node.name === 'speak') return;

            this.$refs.node.classList.remove('non-selectable');
            this.$refs.node.classList.remove('hovered');
        },
        onClick(node, event) {
            let targetNode = node;

            if (
                (node.type === 'word' || node.type === 'number') &&
                node.parent.name !== 'speak' &&
                node.parent.children.length === 1
            ) {
                targetNode = node.parent;
            }

            if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
                this.clearSelection();
                this.selectNode(targetNode);
                return;
            }

            if (event.ctrlKey || event.metaKey) {
                if (this.getSortedSelection().length === 0) {
                    this.selectNode(targetNode);
                    return;
                }

                if (this.isSelected(targetNode)) {
                    return;
                }

                if (this.isNeighbour(targetNode)) {
                    this.selectNode(targetNode);
                }
                return;
            }

            if (event.shiftKey) {
                const selection = this.getSortedSelection().map(item =>
                    item.parent.children.indexOf(item)
                );
                const first = selection[0],
                    last = selection[selection.length - 1];

                const nodeIndex = targetNode.parent.children.indexOf(targetNode);
                const start = Math.min(nodeIndex, first);
                const end = Math.max(nodeIndex, last);

                this.clearSelection();
                for (let i = start; i <= end; i++) {
                    const item = targetNode.parent.children[i];

                    if (item.name === 'space' || item.name === 'break') {
                        continue;
                    }
                    this.selectNode(item);
                }
            }
        },
        isNeighbour(node) {
            const selectionIndexMap = this.getSortedSelection().map(item =>
                item.parent.children.indexOf(item)
            );

            const first = selectionIndexMap[0],
                last = selectionIndexMap[selectionIndexMap.length - 1];

            const nodeIndex = node.parent.children.indexOf(node);

            return (
                nodeIndex === Math.max(first, last) + 2 || nodeIndex === Math.min(first, last) - 2
            );
        }
    }
};
</script>
